import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { isNil } from 'lodash'
import { ArrowDown, ArrowUp, Search, ZoomIn, ZoomOut } from 'lucide-react'

import {
  Button,
  NumberInput,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui'
import { useDocumentVersion } from '@/features/document'
import { formatDisplayDate } from '@/utils'
import { EDocumentType } from '@/utils/constants'

import {
  SIGNED_BLOB_STRUCTURED_URL_REFETCH_INTERVAL,
  VERSION_COMPARISON_DEFAULT_PAGE,
} from '../../constants'
import {
  useAllVersionsReady,
  useLayoutReset,
  useSynchronizedScroll,
} from '../../hooks'
import { HTMLViewer } from '../../html/components'
import { useHtmlController } from '../../html/hooks'
import { IHTMLCompareChangeItem } from '../../html/types'
import { PDFViewer } from '../../pdf/components'
import { usePDFController } from '../../pdf/hooks'
import { IPDFCompareChangeItem } from '../../pdf/types'
import { useVersionComparisonStore } from '../../store'
import { TSelectedVersion } from '../../types'

/**
 * Minimum page for version comparison
 */
export const VERSION_COMPARISON_MIN_PAGE = 1

/**
 * Datetime format for displaying version creation dates
 */
const VERSION_COMPARISON_DATETIME_FORMAT = 'MM/DD/YYYY HH:mm:ss'

type TComparisonLayoutProps = {
  version: TSelectedVersion // The version to display
}

/**
 * Comparison Layout Component
 * Displays a single document version with page navigation controls and document content area.
 *
 * Features:
 * - Supports both PDF and HTML document types
 * - Page navigation (previous/next) with input field
 * - Zoom controls (zoom in/out)
 * - Version metadata display (label, creation date)
 * - Shadow DOM rendering for HTML documents
 * - Synchronized scrolling across multiple versions
 * - Highlight application for version comparison diffs
 * - Scroll-to-highlight synchronization
 *
 * @param version - The version to display in this layout
 */
const ComparisonLayout: React.FC<TComparisonLayoutProps> = ({ version }) => {
  const { t } = useTranslation()
  const {
    documentId,
    documentType,
    compareDocumentDiff,
    setCompareVersionParserUrl,
  } = useVersionComparisonStore()

  // Check if all versions are ready (required for zoom and scroll features)
  const allVersionsReady = useAllVersionsReady()

  /**
   * Determine document type for this viewer
   */
  const isHtmlViewer = React.useMemo(
    () => documentType === EDocumentType.HTML,
    [documentType]
  )
  const isPdfViewer = React.useMemo(
    () => documentType === EDocumentType.PDF,
    [documentType]
  )

  // Synchronized scroll control (only enabled when all versions are ready)
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)
  useSynchronizedScroll(version?.id, scrollContainerRef, allVersionsReady)

  // Reset layout state when dialog closes
  const { data: documentVersion, isLoading: isDocumentLoading } =
    useDocumentVersion(documentId ?? '', version?.id, (query) => {
      // If the signed blob structured url is not available, refetch every 5 seconds
      return query.state.data?.signed_blob_structured_url
        ? false
        : SIGNED_BLOB_STRUCTURED_URL_REFETCH_INTERVAL
    })

  React.useEffect(() => {
    if (!documentVersion?.signed_blob_structured_url) return

    setCompareVersionParserUrl(
      version?.id ?? '',
      documentVersion.signed_blob_structured_url
    )
  }, [
    documentVersion?.signed_blob_structured_url,
    setCompareVersionParserUrl,
    version?.id,
  ])

  // Fetch document content using signed_blob_url
  const signedUrl = React.useMemo(
    () => documentVersion?.signed_blob_url,
    [documentVersion?.signed_blob_url]
  )

  /**
   * Initialize HTML viewer controller with all necessary handlers and state
   */
  const htmlController = useHtmlController({
    signedUrl,
    version,
    allVersionsReady,
    compareDocumentDiff: compareDocumentDiff as IHTMLCompareChangeItem[] | null,
    scrollContainerRef,
    isEnabled: isHtmlViewer,
  })

  /**
   * Initialize PDF viewer controller with page navigation and zoom
   */
  const pdfController = usePDFController({
    signedUrl,
    version,
    allVersionsReady,
    isEnabled: isPdfViewer,
    compareDocumentDiff: compareDocumentDiff as IPDFCompareChangeItem[] | null,
    scrollContainerRef,
  })

  /**
   * Reset layout state (page position, scroll position) when dialog closes
   */
  useLayoutReset({
    setCurrentPage: isPdfViewer ? pdfController.setCurrentPage : undefined,
    scrollContainerRef,
    shadowRootRef: isPdfViewer ? undefined : htmlController.shadowRootRef,
  })

  /**
   * Compute total pages - use current viewer's actual pages, not global total
   * This ensures we display the correct page count for this specific viewer
   */
  const totalPages = React.useMemo(() => {
    return isPdfViewer
      ? pdfController.totalPages
      : VERSION_COMPARISON_DEFAULT_PAGE
  }, [isPdfViewer, pdfController.totalPages])

  /**
   * Compute current page - use PDF controller's clamped page (which is MIN(globalCurrentPage, localTotalPages))
   * For HTML, always use default page
   */
  const currentPage = React.useMemo(() => {
    return isPdfViewer
      ? pdfController.currentPage
      : VERSION_COMPARISON_DEFAULT_PAGE
  }, [isPdfViewer, pdfController.currentPage])

  const [pageInputValue, setPageInputValue] = React.useState<
    number | undefined
  >(currentPage)
  const pageInputRef = React.useRef<HTMLInputElement>(null)

  /**
   * Check if page input/navigation should be disabled
   */
  const isDisabledChangePage = React.useMemo(() => {
    return totalPages <= VERSION_COMPARISON_MIN_PAGE
  }, [totalPages])

  /**
   * Check if next/previous page buttons are enabled
   */
  const canGoNext = React.useMemo(() => {
    return isPdfViewer ? pdfController.canGoNext : false
  }, [isPdfViewer, pdfController.canGoNext])

  const canGoPrevious = React.useMemo(() => {
    return isPdfViewer ? pdfController.canGoPrevious : false
  }, [isPdfViewer, pdfController.canGoPrevious])

  /**
   * Check if zoom buttons should be disabled
   * Disabled if not all versions are ready or document type not supported
   */
  const isZoomDisabled = React.useMemo(() => {
    return !allVersionsReady || (!isHtmlViewer && !isPdfViewer)
  }, [allVersionsReady, isHtmlViewer, isPdfViewer])

  // Sync page input value when global page changes
  React.useEffect(() => {
    setPageInputValue(currentPage)
  }, [currentPage])

  const handlePageChange = React.useCallback(
    (value: number | undefined) => {
      if (isPdfViewer) pdfController.handlePageChange(value)
    },
    [isPdfViewer, pdfController]
  )

  /**
   * Compute clamped page from the current input (ref first, then fallback state)
   */
  const computeClampedPageInput = React.useCallback(() => {
    // Prefer the live DOM value, fall back to local state
    let numValue: number | undefined

    const inputValue = pageInputRef.current?.value
    if (inputValue && inputValue.trim() !== '') {
      const parsed = Number.parseInt(inputValue, 10)
      if (!Number.isNaN(parsed)) numValue = parsed
    }

    numValue ??= pageInputValue

    return Math.min(
      Math.max(
        numValue ?? VERSION_COMPARISON_MIN_PAGE,
        VERSION_COMPARISON_MIN_PAGE
      ),
      totalPages
    )
  }, [pageInputValue, totalPages])

  const handleCommitPageInput = React.useCallback(() => {
    // Clamp and persist the value, then propagate to controllers
    const clampedValue = computeClampedPageInput()
    setPageInputValue(clampedValue)
    handlePageChange(clampedValue)
  }, [computeClampedPageInput, handlePageChange])

  const handlePageInputBlur = React.useCallback(() => {
    handleCommitPageInput()
  }, [handleCommitPageInput])

  const handlePageInputKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleCommitPageInput()
    },
    [handleCommitPageInput]
  )

  const handleNextPage = React.useCallback(() => {
    if (isPdfViewer) pdfController.handleNextPage()
  }, [isPdfViewer, pdfController])

  const handlePreviousPage = React.useCallback(() => {
    if (isPdfViewer) pdfController.handlePreviousPage()
  }, [isPdfViewer, pdfController])

  return (
    <div className="flex-1 h-full bg-primary rounded-md border border-secondary drop-shadow overflow-hidden flex flex-col">
      {(() => {
        // Show loading state while fetching document version metadata
        if (isDocumentLoading) {
          return (
            <div className="flex items-center justify-center p-8 flex-1">
              <Spinner />
            </div>
          )
        }

        // Show error state if document version not found
        if (!documentVersion) {
          return (
            <div className="flex items-center justify-center p-8 flex-1">
              <div className="text-center">
                <p className="text-secondary-700 mb-2">
                  {t('versionComparison.layout.failedToLoadDocument')}
                </p>
                <p className="text-sm text-tertiary-600">
                  {t('versionComparison.layout.pleaseTryAgainLater')}
                </p>
              </div>
            </div>
          )
        }

        // Main content: header with controls + document viewer
        return (
          <div className="size-full flex flex-col">
            {/* Header with version info and page controls */}
            <div className="flex flex-nowrap gap-md items-center px-xl py-lg border-b border-secondary min-w-0 overflow-x-auto overflow-y-hidden flex-shrink-0">
              {/* Version label with creation date tooltip */}
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <span className="text-sm text-primary-900 font-semibold mr-1 text-nowrap">
                    {version?.label ?? '-'}
                  </span>
                </TooltipTrigger>
                {!isNil(documentVersion.created_at) && (
                  <TooltipContent>
                    <span>
                      {formatDisplayDate(
                        documentVersion.created_at,
                        VERSION_COMPARISON_DATETIME_FORMAT
                      )}
                    </span>
                  </TooltipContent>
                )}
              </Tooltip>

              <div className="border-l h-4 border-secondary" />

              {/* Page navigation (PDF only) */}
              <div className="ml-1 flex flex-nowrap gap-2 items-center text-sm text-secondary-700 font-medium">
                <span>{t('versionComparison.layout.page')}</span>
                <NumberInput
                  ref={pageInputRef}
                  className="w-14 h-fit py-1.5 text-sm border-gray-300"
                  value={pageInputValue}
                  min={VERSION_COMPARISON_MIN_PAGE}
                  max={totalPages}
                  onValueChange={setPageInputValue}
                  onBlur={handlePageInputBlur}
                  onKeyDown={handlePageInputKeyDown}
                  changeByButton={false}
                  disabled={isDisabledChangePage}
                />
                <span className="text-nowrap">
                  {t('versionComparison.layout.of', { totalPages })}
                </span>
              </div>

              <div className="border-l h-4 border-secondary" />

              {/* Previous page button */}
              <div className="flex gap-2">
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="text"
                      className="size-6 p-0 gap-0 flex items-center justify-center rounded-sm group"
                      onClick={handleNextPage}
                      disabled={!canGoNext || isDisabledChangePage}
                      title={t('versionComparison.layout.nextPage')}
                      aria-label={t('versionComparison.layout.nextPage')}
                      leadingIcon={
                        <ArrowDown className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500 group-disabled:opacity-50" />
                      }
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>{t('versionComparison.layout.nextPage')}</span>
                  </TooltipContent>
                </Tooltip>

                {/* Next page button */}
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="text"
                      className="size-6 p-0 gap-0 flex items-center justify-center rounded-sm group"
                      onClick={handlePreviousPage}
                      disabled={!canGoPrevious || isDisabledChangePage}
                      title={t('versionComparison.layout.previousPage')}
                      aria-label={t('versionComparison.layout.previousPage')}
                      leadingIcon={
                        <ArrowUp className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500 group-disabled:opacity-50" />
                      }
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>{t('versionComparison.layout.previousPage')}</span>
                  </TooltipContent>
                </Tooltip>
              </div>

              <div className="border-l h-4 border-secondary" />

              {/* Zoom controls (both PDF and HTML) */}
              <div className="flex gap-2">
                {/* Zoom out button */}
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="text"
                      className="size-6 p-0 gap-0 flex items-center justify-center rounded-sm group"
                      onClick={
                        isPdfViewer
                          ? pdfController.zoomOut
                          : htmlController.zoomOut
                      }
                      disabled={
                        isZoomDisabled ||
                        !(isPdfViewer
                          ? pdfController.canZoomOut
                          : htmlController.canZoomOut)
                      }
                      title={t('versionComparison.layout.zoomOut')}
                      aria-label={t('versionComparison.layout.zoomOut')}
                      leadingIcon={
                        <ZoomOut className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500 group-disabled:opacity-50" />
                      }
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>{t('versionComparison.layout.zoomOut')}</span>
                  </TooltipContent>
                </Tooltip>

                {/* Zoom in button */}
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="text"
                      className="size-6 p-0 gap-0 flex items-center justify-center rounded-sm group"
                      onClick={
                        isPdfViewer
                          ? pdfController.zoomIn
                          : htmlController.zoomIn
                      }
                      disabled={
                        isZoomDisabled ||
                        !(isPdfViewer
                          ? pdfController.canZoomIn
                          : htmlController.canZoomIn)
                      }
                      title={t('versionComparison.layout.zoomIn')}
                      aria-label={t('versionComparison.layout.zoomIn')}
                      leadingIcon={
                        <ZoomIn className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500 group-disabled:opacity-50" />
                      }
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>{t('versionComparison.layout.zoomIn')}</span>
                  </TooltipContent>
                </Tooltip>
              </div>

              {/* Search button (disabled/future feature) */}
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="outlined"
                    className="size-6.5 min-w-6.5 p-0 gap-0 flex items-center justify-center rounded-sm bg-primary border-primary ml-auto group"
                    disabled
                    title={t('versionComparison.layout.search')}
                    aria-label={t('versionComparison.layout.search')}
                    leadingIcon={
                      <Search className="size-4 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500 group-disabled:opacity-50" />
                    }
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <span>{t('versionComparison.layout.search')}</span>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Document content area - renders appropriate viewer based on document type */}
            <div
              ref={scrollContainerRef}
              id={version?.id}
              className={`flex-1 p-4 ${allVersionsReady ? 'overflow-auto' : 'overflow-hidden'}`}
            >
              {(() => {
                if (!signedUrl) return null

                switch (documentType) {
                  case EDocumentType.HTML:
                    return (
                      <HTMLViewer
                        content={htmlController.content}
                        isLoading={htmlController.isLoading}
                        error={htmlController.error}
                        shadowHostRef={htmlController.shadowHostRef}
                      />
                    )
                  case EDocumentType.PDF:
                    return (
                      <PDFViewer
                        signedUrl={signedUrl}
                        currentPage={currentPage}
                        scale={pdfController.zoomScale}
                        pdfError={pdfController.pdfError}
                        onDocumentLoadSuccess={
                          pdfController.handleDocumentLoadSuccess
                        }
                        onDocumentLoadError={
                          pdfController.handleDocumentLoadError
                        }
                        onPageRenderSuccess={
                          pdfController.handlePageRenderSuccess
                        }
                      />
                    )
                  default:
                    return null
                }
              })()}
            </div>
          </div>
        )
      })()}
    </div>
  )
}

/**
 * Memoized version of ComparisonLayout to prevent unnecessary re-renders
 */
const ComparisonLayoutMemoized = React.memo(ComparisonLayout)
ComparisonLayoutMemoized.displayName = 'ComparisonLayout'

export { ComparisonLayoutMemoized as ComparisonLayout }
